/**
 * TRUS-719: Organization settings → ECM reminders. One org switch: whether ECM
 * reminders also go to members by text / email (TRUS-704's
 * organization_reminder_config.member_sms_email_enabled).
 *
 * GET / PATCH /api/ecm/reminder-settings → {member_sms_email_enabled, can_edit}.
 * The server says who may change it (org admins); everyone else sees it
 * read-only. The setting belongs to the ACTIVE organization (X-CBO-ID), so a
 * page about another org says to switch first. `useEcmReminderSettings`
 * returns `data === null` when the endpoint is not deployed: the caller hides
 * the section.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/use-toast';
import { ecmReminderSettingsApi } from '@/services/inboxPhase4Api';

export function ecmReminderSettingsKey(): unknown[] {
  return ['ecm-reminder-settings', typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : null];
}

/** The active org's ECM reminder switch; `data === null` when the endpoint is missing. */
export function useEcmReminderSettings(enabled = true) {
  return useQuery({
    queryKey: ecmReminderSettingsKey(),
    queryFn: () => ecmReminderSettingsApi.get(),
    staleTime: 60_000,
    retry: false,
    enabled,
  });
}

interface EcmReminderSettingsPanelProps {
  /** The org the surrounding page is about; the setting applies to the active org. */
  organizationId?: number;
}

export function EcmReminderSettingsPanel({ organizationId }: EcmReminderSettingsPanelProps) {
  const { t } = useAppTranslation();
  const qc = useQueryClient();
  const { data: settings, isLoading, isError } = useEcmReminderSettings();
  const activeOrgId = Number(typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : NaN);
  const otherOrg = organizationId != null && Number.isFinite(activeOrgId) && organizationId !== activeOrgId;

  const save = useMutation({
    mutationFn: (enabled: boolean) => ecmReminderSettingsApi.update({ member_sms_email_enabled: enabled }),
    onSuccess: (next) => {
      qc.setQueryData(ecmReminderSettingsKey(), next);
      toast({ title: t('admin.ecmReminders.saved', 'ECM reminder setting saved') });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (settings === null) return null;
  if (isError || !settings) {
    return (
      <p className="text-sm text-destructive" role="alert">
        {t('admin.ecmReminders.loadFailed', 'Could not load the ECM reminder setting.')}
      </p>
    );
  }

  const canEdit = settings.can_edit && !otherOrg;

  return (
    <section className="space-y-4" data-testid="ecm-reminder-settings">
      <div>
        <h2 className="text-lg font-semibold">{t('admin.ecmReminders.title', 'ECM reminders')}</h2>
        <p className="text-sm text-muted-foreground">
          {t('admin.ecmReminders.subtitle', 'Whether ECM reminders also reach members, not only staff.')}
        </p>
      </div>

      {otherOrg && (
        <p
          className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-100"
          data-testid="ecm-reminders-other-org"
        >
          {t('admin.ecmReminders.otherOrg', 'This setting belongs to the organization you are working in. Switch to this organization to change it.')}
        </p>
      )}
      {!otherOrg && !canEdit && (
        <p className="text-xs text-muted-foreground" data-testid="ecm-reminders-read-only">
          {t('admin.ecmReminders.readOnly', 'Only organization admins can change this setting.')}
        </p>
      )}

      <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
        <div>
          <label htmlFor="ecm-reminders-member-sms-email" className="block text-sm font-medium">
            {t('admin.ecmReminders.toggle', 'Send ECM reminders to members by text/email')}
          </label>
          <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-muted-foreground" data-testid="ecm-reminders-help">
            <li>{t('admin.ecmReminders.helpMemberOnly', 'Only reminders meant for the member are sent; staff reminders stay with staff.')}</li>
            <li>{t('admin.ecmReminders.helpOverdue', 'Overdue reminders are skipped.')}</li>
            <li>{t('admin.ecmReminders.helpOptOut', 'Member opt-outs and quiet hours are respected.')}</li>
          </ul>
        </div>
        <Switch
          id="ecm-reminders-member-sms-email"
          checked={settings.member_sms_email_enabled}
          disabled={!canEdit || save.isPending}
          onCheckedChange={(checked) => save.mutate(checked)}
          data-testid="ecm-reminders-toggle"
        />
      </div>

      {save.isError && (
        <p className="text-sm text-destructive" role="alert">
          {t('admin.ecmReminders.saveFailed', 'Could not save the ECM reminder setting.')}
        </p>
      )}
    </section>
  );
}

export default EcmReminderSettingsPanel;
